import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

interface SystemSetting {
  id: string;
  key: string;
  value: any;
  description?: string;
  category?: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

export const useSystemSettings = () => {
  return useQuery({
    queryKey: ["system-settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("system_settings")
        .select("*");
      
      if (error) throw error;
      return data as SystemSetting[];
    },
  });
};

export const useSystemSetting = (key: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ["system-setting", key],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("system_settings")
        .select("*")
        .eq("key", key)
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data as SystemSetting | null;
    },
    enabled: options?.enabled !== false,
  });
};

export const useUpdateSystemSetting = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ key, value, description, category }: { 
      key: string; 
      value: any; 
      description?: string; 
      category?: string; 
    }) => {
      const { data, error } = await supabase
        .from("system_settings")
        .upsert({
          key,
          value,
          description,
          category,
        }, {
          onConflict: 'key'
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["system-settings"] });
      toast({
        title: "Configuração atualizada",
        description: "A configuração do sistema foi atualizada com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao atualizar configuração",
        description: "Ocorreu um erro ao tentar atualizar a configuração do sistema.",
        variant: "destructive",
      });
    },
  });
};